import { useState } from 'react';
import loadable from '@loadable/component';
import classNames from 'classnames';

import {
    ClickElement,
    GA4EventName,
    GA4FeatureCategory,
    trackAnalyticsEvent,
} from '@jsmdg/tracking';
import { InfoIcon, PureButton } from '@jsmdg/yoshi';
import { useProduct } from '../../hooks/useProduct';
import { getDiscountCodeCampaignBadgeStyle } from './getDiscountCodeCampaignBadgeStyle';
import styles from './DiscountCodeCampaignBadge.module.scss';

const LoadableDiscountCodeCampaignModal = loadable(
    async () => import('../DiscountCodeCampaignModal/DiscountCodeCampaignModal'),
);

async function trackBadgeClick(clickText: string): Promise<void> {
    await window.yieldToMainThread();
    trackAnalyticsEvent({
        eventData: {
            eventName: GA4EventName.ClickButton,
            feature_category: GA4FeatureCategory.ProductdetailBuybox,
            click_element: ClickElement.BuddyVoucherInfo,
            click_text: clickText,
        },
    });
}

type DiscountCodeCampaignBadgeProps = {
    readonly className?: string;
};

const DiscountCodeCampaignBadge = ({
    className,
}: DiscountCodeCampaignBadgeProps): JSX.Element | null => {
    const [showModal, setShowModal] = useState(false);
    const product = useProduct();
    const discountBadge = product?.discountBadge;

    if (!discountBadge) {
        return null;
    }

    const badgeText = discountBadge.descriptionLabel ?? '';
    const badgeStyle = getDiscountCodeCampaignBadgeStyle(discountBadge);

    const toggleModal = (): void => {
        setShowModal(!showModal);
        if (!showModal) {
            void trackBadgeClick(badgeText);
        }
    };

    return (
        <>
            {showModal && <LoadableDiscountCodeCampaignModal onRequestClose={toggleModal} />}
            <PureButton
                className={classNames('d-flex', className, styles.badgeContainer)}
                onClick={toggleModal}
            >
                <div
                    className={classNames(
                        'd-flex align-items-center px-1x py-0-5x',
                        styles.badgeTextContainer,
                    )}
                    style={badgeStyle}
                >
                    <div className={classNames('font-complementary mr-0-5x', styles.badgeText)}>
                        {badgeText}
                    </div>
                    <InfoIcon color={discountBadge.textColor} size={24} />
                </div>
            </PureButton>
        </>
    );
};

export { DiscountCodeCampaignBadge };
